import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import * as authApi from '../services/authApi';
import type { UserProfile } from '../services/authApi';
import type { Session } from '@supabase/supabase-js';
import { clearAllCachesOnLogout } from '../services/cacheCleanupService';
import { supabase } from '../lib/supabase/client';
import { mapAuthError } from '../services/errorMap';
import { analytics } from '../services/analytics';

/**
 * Cross-tab broadcast channel for auth events.
 * When one tab logs out (or signs in), the others mirror the change so the
 * user does not see a stale "logged in" UI in another tab. BroadcastChannel
 * is supported in all current browsers; the helper falls back to a no-op
 * shape on the rare environment that lacks it (older Safari, some test
 * jsdom setups) so the rest of the store still works.
 */
type AuthBroadcastMessage = { type: 'logout' } | { type: 'session-refreshed' };
const AUTH_BROADCAST_NAME = 'edouard-auth';
function createAuthChannel(): BroadcastChannel | null {
  if (typeof window === 'undefined' || typeof BroadcastChannel === 'undefined') {
    return null;
  }
  try {
    return new BroadcastChannel(AUTH_BROADCAST_NAME);
  } catch {
    return null;
  }
}

interface AuthState {
  // State
  user: UserProfile | null;
  session: Session | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isInitialized: boolean; // Track if initial auth check is complete
  /**
   * Localized default message ready to render (FR fallback). Use this when
   * the consumer doesn't have access to `useTranslation()`.
   */
  error: string | null;
  /**
   * Stable error code from `mapAuthError` (e.g. `'auth/wrong-password'`).
   * Components with i18n support should prefer
   * `t(\`auth.errors.\${codeToKey(errorCode)}\`)` over `error` so users
   * see the message in their selected language.
   */
  errorCode: string | null;

  // Actions
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, username?: string) => Promise<void>;
  logout: () => Promise<void>;
  loadUser: () => Promise<void>;
  clearError: () => void;
  /**
   * Wire up the Supabase auth-state listener and the cross-tab broadcast
   * channel. Idempotent — calling twice returns the same teardown. Call
   * once at app boot (e.g. from App.tsx or an AuthProvider).
   */
  initAuthListeners: () => () => void;
}

/**
 * Build the user-fragment of the store from a Supabase session. Used by the
 * auth listener so we never desync the cached `user` from the live session.
 * Falls back to the metadata payload on the session if a fresh
 * `getProfile()` round-trip is not desirable here (the listener fires
 * frequently — TOKEN_REFRESHED ticks every hour or so).
 */
function userFromSession(session: Session | null): UserProfile | null {
  if (!session?.user) return null;
  return {
    id: session.user.id,
    email: session.user.email || '',
    username: session.user.user_metadata?.username || null,
    displayName: session.user.user_metadata?.display_name || null,
    avatarUrl: session.user.user_metadata?.avatar_url || null,
    emailVerified: !!session.user.email_confirmed_at,
    subscriptionTier: session.user.user_metadata?.subscription_tier || 'free',
    createdAt: session.user.created_at,
  };
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      session: null,
      isAuthenticated: false,
      isLoading: false,
      isInitialized: false,
      error: null,
      errorCode: null,


      // Login
      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null, errorCode: null });

        try {
          const data = await authApi.login({ email, password });

          set({
            user: data.user,
            session: data.session,
            isAuthenticated: true,
            isLoading: false,
            isInitialized: true,
            error: null,
            errorCode: null,
          });
          analytics.event('auth_login_success');
        } catch (error: unknown) {
          // Translate Supabase's raw error string into a user-friendly
          // message + stable code. The default is FR-localized; consumers
          // with i18n call `t(...)` against the messageKey.
          const mapped = mapAuthError(error);

          set({
            isLoading: false,
            error: mapped.messageDefault,
            errorCode: mapped.code,
          });

          // Tag the failure so trends in "trending up" failure modes can be
          // monitored (e.g. a sudden spike of `auth/wrong-password` may
          // indicate a credential-stuffing attempt). Code is stable across
          // localizations — safe for analytics aggregation.
          analytics.event('auth_login_failed', { code: mapped.code });

          throw error;
        }
      },

      // Register
      register: async (email: string, password: string, username?: string) => {
        set({ isLoading: true, error: null, errorCode: null });

        try {
          const data = await authApi.register({ email, password, username });

          set({
            user: data.user,
            session: data.session,
            isAuthenticated: true,
            isLoading: false,
            isInitialized: true,
            error: null,
            errorCode: null,
          });
          analytics.event('auth_register_success');
        } catch (error: unknown) {
          const mapped = mapAuthError(error);

          set({
            isLoading: false,
            error: mapped.messageDefault,
            errorCode: mapped.code,
          });

          analytics.event('auth_register_failed', { code: mapped.code });

          throw error;
        }
      },

      // Logout
      logout: async () => {
        set({ isLoading: true });

        try {
          await authApi.logout();
        } catch (error) {
          console.error('Logout error:', error);
        } finally {
          // Clear all local caches (preserves language, theme, remember-me)
          clearAllCachesOnLogout();

          set({
            user: null,
            session: null,
            isAuthenticated: false,
            isLoading: false,
            isInitialized: true,
            error: null,
          });
        }
      },

      // Load user profile
      loadUser: async () => {
        // Don't set isLoading if already initialized to avoid UI flicker on refresh
        const { isInitialized } = get();
        if (!isInitialized) {
          set({ isLoading: true });
        }

        try {
          // Check if session exists
          const session = await authApi.getSession();
          if (!session) {
            set({ isAuthenticated: false, user: null, session: null, isLoading: false, isInitialized: true });
            return;
          }

          // Try to get fresh user profile from Supabase
          let user: UserProfile | null = null;
          try {
            user = await authApi.getProfile();
          } catch (profileError) {
            console.warn('loadUser: getProfile failed, using session user data:', profileError);
            // Fallback: Use user data from session if profile fetch fails
            if (session.user) {
              user = {
                id: session.user.id,
                email: session.user.email || '',
                username: session.user.user_metadata?.username || null,
                displayName: session.user.user_metadata?.display_name || null,
                avatarUrl: session.user.user_metadata?.avatar_url || null,
                emailVerified: !!session.user.email_confirmed_at,
                subscriptionTier: session.user.user_metadata?.subscription_tier || 'free',
                createdAt: session.user.created_at,
              };
            }
          }

          if (user) {
            set({
              user,
              session,
              isAuthenticated: true,
              isLoading: false,
              isInitialized: true,
              error: null,
            });
          } else {
            // No user data available
            console.error('loadUser: No user data available');
            set({
              user: null,
              session: null,
              isAuthenticated: false,
              isLoading: false,
              isInitialized: true,
            });
          }
        } catch (error) {
          // Session retrieval failed
          console.error('loadUser error:', error);
          set({
            user: null,
            session: null,
            isAuthenticated: false,
            isLoading: false,
            isInitialized: true,
          });
        }
      },

      // Clear error
      clearError: () => {
        set({ error: null, errorCode: null });
      },

      // Wire up Supabase + BroadcastChannel listeners. Idempotent.
      initAuthListeners: () => {
        const initState = (
          get as () => AuthState & { __authListenersTeardown?: () => void }
        )();
        if (initState.__authListenersTeardown) {
          return initState.__authListenersTeardown;
        }

        // 1) Supabase auth listener — narrow scope on purpose.
        //    App.tsx already subscribes to `SIGNED_IN` / `INITIAL_SESSION`
        //    to drive `loadUser()` and the cloud-sync conflict UI. Here we
        //    only handle the events App.tsx does NOT:
        //      - SIGNED_OUT       : clear local state explicitly (don't wait
        //                            for loadUser() to discover the absence
        //                            of a session).
        //      - TOKEN_REFRESHED  : refresh the cached session object so
        //                            authenticated requests use the fresh
        //                            access token.
        //      - USER_UPDATED     : mirror profile metadata changes (email,
        //                            display name) without a getProfile()
        //                            round-trip.
        //    This is deliberately silent on SIGNED_IN / INITIAL_SESSION to
        //    avoid racing the App.tsx listener.
        const { data: subscription } = supabase.auth.onAuthStateChange(
          (event, session) => {
            switch (event) {
              case 'TOKEN_REFRESHED':
              case 'USER_UPDATED': {
                const user = userFromSession(session);
                if (session && user) {
                  set({
                    user,
                    session,
                    isAuthenticated: true,
                    isInitialized: true,
                    isLoading: false,
                  });
                }
                break;
              }
              case 'SIGNED_OUT': {
                set({
                  user: null,
                  session: null,
                  isAuthenticated: false,
                  isInitialized: true,
                  isLoading: false,
                  error: null,
                });
                break;
              }
              default:
                // SIGNED_IN, INITIAL_SESSION, PASSWORD_RECOVERY: handled
                // by App.tsx (or recovery UI). Do not duplicate work.
                break;
            }
          }
        );

        // 2) Cross-tab broadcast — when this tab logs out, tell the others
        //    to clear their local state too. Listening to incoming messages
        //    drives `signOut()` (without rebroadcasting) so we don't loop.
        const channel = createAuthChannel();
        let remoteLogoutInProgress = false;
        if (channel) {
          channel.onmessage = async (e: MessageEvent<AuthBroadcastMessage>) => {
            if (e.data?.type === 'logout') {
              remoteLogoutInProgress = true;
              try {
                clearAllCachesOnLogout();
                set({
                  user: null,
                  session: null,
                  isAuthenticated: false,
                  isInitialized: true,
                  isLoading: false,
                  error: null,
                });
                await supabase.auth.signOut().catch(() => {});
              } finally {
                remoteLogoutInProgress = false;
              }
            }
          };

          // Wrap the existing `logout` to broadcast on outgoing logouts.
          // We monkey-patch the local action so callers don't have to know
          // about the channel — the broadcast is an implementation detail.
          const originalLogout = (get as () => AuthState).bind(undefined)().logout;
          set({
            logout: async () => {
              await originalLogout();
              if (!remoteLogoutInProgress) {
                try {
                  channel.postMessage({ type: 'logout' });
                } catch {
                  /* channel closed mid-flight — fine, locally already done */
                }
              }
            },
          });
        }

        const teardown = () => {
          subscription?.subscription?.unsubscribe?.();
          channel?.close();
          set({
            // Clear the marker so a re-init can re-attach (e.g. HMR).
            ...({ __authListenersTeardown: undefined } as Partial<AuthState>),
          });
        };
        // Stash the teardown on the state so a re-call returns it instead
        // of double-subscribing. Using a private field keeps it out of the
        // public state surface but still survives the `set` call.
        set({
          ...({ __authListenersTeardown: teardown } as unknown as Partial<AuthState>),
        });
        return teardown;
      },
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        // Persist user profile and auth state (tokens are managed by Supabase in localStorage)
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
